import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getHealth, dealDamage, healHP, createHealthStream } from '../api/health';
import { getAuthToken } from '../utils/authStorage';

// TEMP hotfix (2026-07-10): master switch for the death / resuscitate UI. While
// false, the client never enters the dead state — a killing blow just heals to
// full (the backend does the same via config.death_enabled). Flip back to true
// alongside DEATH_ENABLED on the backend when the resuscitate rehaul lands.
const DEATH_ENABLED = false;

const HealthContext = createContext(null);

export function HealthProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [hp, setHp] = useState(100);
  const [maxHp, setMaxHp] = useState(100);
  const [isDead, setIsDead] = useState(false);
  const [trainingSinceDeath, setTrainingSinceDeath] = useState(0);
  const [deathDeckId, setDeathDeckId] = useState(null);
  const [deathBossName, setDeathBossName] = useState(null);
  const [deathSource, setDeathSource] = useState(null);
  const [damageAnim, setDamageAnim] = useState(false);
  const [healAnim, setHealAnim] = useState(false);
  const [hpMaskThreshold, setHpMaskThreshold] = useState(0);
  const [passiveFountains, setPassiveFountains] = useState({ passive_fountain_count: 0, passive_heal_per_hour: 0 });
  const [overworldInvincible, setOverworldInvincible] = useState(false);
  const sseRef = useRef(null);
  const prevHpRef = useRef(null);

  const fetchHealth = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getHealth();
      setHp(data.current_hp);
      setMaxHp(data.max_hp);
      setIsDead(DEATH_ENABLED && data.is_dead);
      setTrainingSinceDeath(data.training_since_death || 0);
      setDeathDeckId(data.death_deck_id || null);
      setDeathBossName(data.death_boss_name || null);
      setDeathSource(data.death_source || null);
      setHpMaskThreshold(data.hp_mask_threshold || 0);
      // Idempotent: passive_fountains is a fresh object each poll; committing it
      // unconditionally re-renders every useHealth() consumer (HP HUD) on the
      // slow poll even when unchanged (glassmorphism re-composite flicker).
      if (data.passive_fountains) {
        setPassiveFountains((prev) => (
          prev
          && prev.passive_fountain_count === data.passive_fountains.passive_fountain_count
          && prev.passive_heal_per_hour === data.passive_fountains.passive_heal_per_hour
        ) ? prev : data.passive_fountains);
      }
      setOverworldInvincible(Boolean(data.overworld_invincible));
    } catch (err) {
      // Silently fail
    }
  }, [isAuthenticated]);

  // SSE connection for real-time HP updates
  useEffect(() => {
    if (!isAuthenticated) return;

    // Defer initial health fetch by 2s to reduce tunnel contention on mount.
    // SSE stream below provides real-time hp/death updates in the meantime.
    const healthTimeout = setTimeout(fetchHealth, 2000);

    const token = getAuthToken();
    if (!token) return;

    const sse = createHealthStream(token);
    sseRef.current = sse;

    sse.onmessage = ({ data }) => {
      if (data.hp != null) setHp(data.hp);
      if (data.max_hp != null) setMaxHp(data.max_hp);
      if (data.is_dead != null) {
        setIsDead(DEATH_ENABLED && data.is_dead);
        if (DEATH_ENABLED && data.is_dead) {
          // Refetch full state to get death info
          fetchHealth();
        }
      }
    };

    sse.onerror = () => {
      // SSE will auto-reconnect; do a full fetch to resync
      fetchHealth();
    };

    // Slow poll as fallback for passive fountain updates + death info
    // (SSE only pushes hp/max_hp/is_dead from mutations)
    const slowPoll = setInterval(fetchHealth, 120000);

    return () => {
      clearTimeout(healthTimeout);
      sse.close();
      sseRef.current = null;
      clearInterval(slowPoll);
    };
  }, [isAuthenticated, fetchHealth]);

  // Trigger heal animation when HP increases from SSE
  useEffect(() => {
    if (prevHpRef.current != null && hp > prevHpRef.current && !isDead) {
      setHealAnim(false);
      requestAnimationFrame(() => {
        setHealAnim(true);
        setTimeout(() => setHealAnim(false), 600);
      });
    }
    prevHpRef.current = hp;
  }, [hp, isDead]);

  const takeDamage = useCallback(async (amount, source) => {
    // Optimistic HP decrement
    setHp(prev => Math.max(0, prev - amount));

    // Trigger shake animation
    setDamageAnim(true);
    setTimeout(() => setDamageAnim(false), 500);

    try {
      const result = await dealDamage(amount, source);
      // SSE will push authoritative HP, but set immediately for responsiveness
      setHp(result.current_hp);
      if (DEATH_ENABLED && result.died) {
        setIsDead(true);
        fetchHealth();
      }
    } catch (err) {
      console.warn('Damage call failed, SSE will sync:', err?.message);
    }
  }, [fetchHealth]);

  // Allow external systems (e.g. boss combat) to override displayed HP in real-time
  const overrideHp = useCallback((current, max) => {
    if (current != null) setHp(current);
    if (max != null) setMaxHp(max);
    if (DEATH_ENABLED && current != null && current <= 0) setIsDead(true);
  }, []);

  // Optimistic heal notification — kept for immediate feedback before SSE arrives
  const healTimerRef = useRef(null);
  const notifyHeal = useCallback((amount) => {
    if (amount <= 0 || isDead) return;
    setHp(prev => Math.min(maxHp, prev + amount));
  }, [maxHp, isDead]);

  const heal = useCallback(async (amount, source) => {
    // Optimistic HP increment
    setHp(prev => Math.min(maxHp, prev + amount));

    try {
      const result = await healHP(amount, source);
      if (result.current_hp != null) {
        setHp(result.current_hp);
      }
    } catch (err) {
      console.warn('Heal call failed, SSE will sync:', err?.message);
    }
  }, [maxHp]);

  const value = useMemo(() => ({
    hp,
    maxHp,
    isDead,
    trainingSinceDeath,
    deathDeckId,
    deathBossName,
    deathSource,
    passiveFountains,
    overworldInvincible,
    hpMaskThreshold,
    damageAnim,
    healAnim,
    takeDamage,
    heal,
    notifyHeal,
    overrideHp,
    refetchHealth: fetchHealth,
  }), [
    hp,
    maxHp,
    isDead,
    trainingSinceDeath,
    deathDeckId,
    deathBossName,
    deathSource,
    passiveFountains,
    overworldInvincible,
    hpMaskThreshold,
    damageAnim,
    healAnim,
    takeDamage,
    heal,
    notifyHeal,
    overrideHp,
    fetchHealth,
  ]);

  return (
    <HealthContext.Provider value={value}>
      {children}
    </HealthContext.Provider>
  );
}

export function useHealth() {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error('useHealth must be used within HealthProvider');
  }
  return context;
}
